/**
 * Signup source attribution (UTM tracking) — sibling of the referral flow
 * in features/affiliate/capture-referral.ts and deliberately shaped the
 * same way:
 *
 *   1. captureAttribution() runs on EVERY initial page load (signed-out or
 *      not). First touch wins: if the URL carries UTM params or an external
 *      referrer AND nothing is stashed yet, stash a JSON snapshot in
 *      localStorage. The marketing site sets the same snapshot as a
 *      `dca_attr` cookie on .dealcloserautopilot.com (see index.html), so
 *      a visitor who lands on the marketing page and then clicks through
 *      to /signup still carries their source. localStorage (a direct-to-app
 *      link, e.g. an IG bio link straight to /signup?utm_source=instagram)
 *      is read first, then the cookie.
 *
 *   2. attributePendingSignupSource(apiFetch) runs once the user is
 *      AUTHENTICATED. It POSTs the snapshot to /api/auth/attribution; the
 *      backend stamps it only if the user has no attribution row yet.
 *      On ANY terminal response (stamped, already stamped, invalid) the
 *      stash and cookie are cleared so we never pester on later loads.
 *
 * Failure modes are silent, exactly like the referral flow — attribution
 * must never break login or signup.
 */

const STORAGE_KEY = 'dca_attribution'
const COOKIE_NAME = 'dca_attr'

export interface AttributionSnapshot {
  readonly utm_source: string | null
  readonly utm_medium: string | null
  readonly utm_campaign: string | null
  readonly utm_content: string | null
  readonly utm_term: string | null
  readonly referrer: string | null
  readonly landing_page: string | null
  readonly captured_at: string | null
}

/** Clamp free-text URL params to something safe to store and render. */
function clean(v: string | null | undefined): string | null {
  if (!v) return null
  const trimmed = v.trim().slice(0, 200)
  return trimmed.length > 0 ? trimmed : null
}

function readCookieSnapshot(): AttributionSnapshot | null {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`))
    if (!m || !m[1]) return null
    const parsed = JSON.parse(decodeURIComponent(m[1])) as Partial<AttributionSnapshot>
    return normalize(parsed)
  } catch {
    return null
  }
}

function normalize(raw: Partial<AttributionSnapshot>): AttributionSnapshot | null {
  const snap: AttributionSnapshot = {
    utm_source: clean(raw.utm_source),
    utm_medium: clean(raw.utm_medium),
    utm_campaign: clean(raw.utm_campaign),
    utm_content: clean(raw.utm_content),
    utm_term: clean(raw.utm_term),
    referrer: clean(raw.referrer),
    landing_page: clean(raw.landing_page),
    captured_at: clean(raw.captured_at),
  }
  return snap.utm_source || snap.referrer ? snap : null
}

/**
 * Stash this load's UTM params / referrer in localStorage if nothing is
 * stashed yet. Called from the auth provider before Clerk's redirect dance
 * eats the URL. Safe to call repeatedly.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return
  try {
    if (window.localStorage.getItem(STORAGE_KEY)) return
    const p = new URLSearchParams(window.location.search)
    const ref = document.referrer || ''
    const internal =
      ref.includes('dealcloserautopilot.com') || ref.includes(window.location.host)
    const snap = normalize({
      utm_source: p.get('utm_source'),
      utm_medium: p.get('utm_medium'),
      utm_campaign: p.get('utm_campaign'),
      utm_content: p.get('utm_content'),
      utm_term: p.get('utm_term'),
      referrer: internal ? null : ref || null,
      landing_page: window.location.pathname,
      captured_at: new Date().toISOString(),
    })
    if (!snap) return
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snap))
  } catch {
    // localStorage disabled / SSR — silently skip.
  }
}

/** localStorage first (direct app links), then the marketing-site cookie. */
export function getStashedAttribution(): AttributionSnapshot | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const snap = normalize(JSON.parse(raw) as Partial<AttributionSnapshot>)
      if (snap) return snap
    }
  } catch {
    // fall through to cookie
  }
  return readCookieSnapshot()
}

export function clearStashedAttribution(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
  try {
    document.cookie = `${COOKIE_NAME}=; domain=.dealcloserautopilot.com; path=/; max-age=0`
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`
  } catch {
    // ignore
  }
}

type FetchResult<T> = {
  readonly success: boolean
  readonly data?: T
  readonly error?: string
}

type Fetcher = <T>(
  path: string,
  opts?: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
    body?: unknown
    headers?: Record<string, string>
  },
) => Promise<FetchResult<T>>

/**
 * One-shot post-auth attribution. The backend keeps the FIRST row per user
 * and ignores everything after, so duplicate calls are harmless.
 */
export async function attributePendingSignupSource(apiFetch: Fetcher): Promise<void> {
  const snap = getStashedAttribution()
  if (!snap) return
  try {
    await apiFetch('/api/auth/attribution', { method: 'POST', body: snap })
  } catch {
    // Best effort — do not retry loops on network errors.
  }
  clearStashedAttribution()
}
